import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  TrendingUp, TrendingDown, Receipt, Loader2, AlertTriangle,
  Users, Tag, ArrowRight,
} from "lucide-react";

const EUR = (n) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
    .format(Number(n) || 0);

const MONTH_LABEL = (lang) => (ym) => {
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(lang === "es" ? "es-ES" : "ca-ES",
    { month: "short", year: "2-digit" });
};

export default function ExpenseDashboard() {
  const { lang } = useI18n();
  const tr = (ca, es) => (lang === "es" ? es : ca);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmtMonth = MONTH_LABEL(lang);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/received-invoices/expense-dashboard");
        if (alive) setData(r.data);
      } catch (e) {
        // Silently degrade — the dashboard parent will keep working.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-6 text-center text-slate-500">
        <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
        <p className="text-sm">{tr("Carregant despeses…", "Cargando gastos…")}</p>
      </div>
    );
  }
  if (!data) return null;

  const cur = data.current || {};
  const prev = data.previous || {};
  const delta = data.delta_total || 0;
  const deltaUp = delta > 0;
  const pct = data.delta_pct;

  // Build sparkline (last 12 months)
  const series = data.rolling_12m || [];
  const max = Math.max(1, ...series.map((s) => s.total || 0));
  const empty = (cur.count || 0) === 0;

  return (
    <div className="card-surface overflow-hidden" data-testid="expense-dashboard">
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Receipt size={16} className="text-blue-300" />
          <h2 className="font-display text-sm font-semibold tracking-tight text-white">
            {tr("Despeses del mes", "Gastos del mes")}
          </h2>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            {fmtMonth(data.month)}
          </span>
        </div>
        <Link
          to="/app/received-invoices"
          className="text-[11px] text-blue-300 hover:text-blue-200 flex items-center gap-1"
          data-testid="expense-see-all"
        >
          {tr("Veure totes", "Ver todas")} <ArrowRight size={11} />
        </Link>
      </div>

      {empty ? (
        <div className="p-8 text-center text-slate-500">
          <Receipt className="w-10 h-10 mx-auto mb-2 text-slate-600" />
          <p className="text-sm">
            {tr("Cap factura aquest mes encara.", "Ninguna factura este mes todavía.")}
          </p>
          {data.pending_ocr > 0 && (
            <Link
              to="/app/mailbox"
              className="inline-flex items-center gap-2 mt-3 text-xs text-amber-300 hover:text-amber-200"
              data-testid="expense-pending-ocr-link"
            >
              <AlertTriangle size={12} />
              {tr(
                `${data.pending_ocr} factures importades sense OCR — processa-les a la bústia`,
                `${data.pending_ocr} facturas importadas sin OCR — procesalas en el buzón`
              )}
            </Link>
          )}
        </div>
      ) : (
        <div className="p-5 space-y-5">
          {/* Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat
              label={tr("Total", "Total")}
              value={EUR(cur.total)}
              trend={pct}
              trendUp={deltaUp}
              testid="expense-stat-total"
            />
            <Stat
              label={tr("Factures", "Facturas")}
              value={cur.count}
              trend={prev.count ? ((cur.count - prev.count) / prev.count) * 100 : null}
              trendUp={cur.count > prev.count}
              testid="expense-stat-count"
            />
            <Stat
              label={tr("Proveïdors", "Proveedores")}
              value={cur.supplier_count}
              icon={<Users size={11} className="text-slate-500" />}
              testid="expense-stat-suppliers"
            />
            <Stat
              label={tr("IVA suportat", "IVA soportado")}
              value={EUR(cur.vat)}
              testid="expense-stat-vat"
            />
          </div>

          {/* Delta vs previous */}
          {pct !== null && pct !== undefined && (
            <div className="text-xs text-slate-400 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5">
              {deltaUp
                ? <TrendingUp size={12} className="text-rose-300" />
                : <TrendingDown size={12} className="text-emerald-300" />}
              <span className={deltaUp ? "text-rose-300" : "text-emerald-300"}>
                {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
              </span>
              <span>vs {fmtMonth(data.previous_month)} ({EUR(prev.total)})</span>
            </div>
          )}

          {/* 12-month sparkline */}
          {series.length > 1 && (
            <div>
              <div className="flex items-end gap-1 h-16 mb-1.5" data-testid="expense-sparkline">
                {series.map((s, i) => {
                  const h = Math.max(4, (s.total / max) * 64);
                  const isCurrent = s.month === data.month;
                  return (
                    <div
                      key={s.month}
                      className="flex-1 relative group"
                      title={`${fmtMonth(s.month)}: ${EUR(s.total)}`}
                    >
                      <div
                        className={`w-full rounded-t transition-colors ${
                          isCurrent
                            ? "bg-gradient-to-t from-blue-600 to-blue-400"
                            : "bg-slate-700 group-hover:bg-slate-600"
                        }`}
                        style={{ height: `${h}px` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[9px] text-slate-600 font-mono">
                <span>{series.length > 0 && fmtMonth(series[0].month)}</span>
                <span>{fmtMonth(data.month)}</span>
              </div>
            </div>
          )}

          {/* Top suppliers + categories */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.top_suppliers && data.top_suppliers.length > 0 && (
              <div>
                <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                  <Users size={10} /> {tr("Top proveïdors", "Top proveedores")}
                </h3>
                <div className="space-y-1.5" data-testid="expense-top-suppliers">
                  {data.top_suppliers.map((s, i) => {
                    const pct = cur.total > 0 ? (s.total / cur.total) * 100 : 0;
                    return (
                      <div key={i} className="text-xs">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-slate-300 truncate flex-1 mr-2">{s.name}</span>
                          <span className="font-mono text-slate-200">{EUR(s.total)}</span>
                        </div>
                        <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-400"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {data.by_category && data.by_category.length > 0 && (
              <div>
                <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                  <Tag size={10} /> {tr("Per categoria", "Por categoría")}
                </h3>
                <div className="space-y-1.5" data-testid="expense-by-category">
                  {data.by_category.slice(0, 5).map((c, i) => {
                    const pct = cur.total > 0 ? (c.total / cur.total) * 100 : 0;
                    return (
                      <div key={i} className="text-xs">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-slate-300 truncate flex-1 mr-2">
                            {c.name}
                            <span className="text-slate-600 ml-1">({c.count})</span>
                          </span>
                          <span className="font-mono text-slate-200">{EUR(c.total)}</span>
                        </div>
                        <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Pending OCR banner */}
          {data.pending_ocr > 0 && (
            <Link
              to="/app/mailbox"
              data-testid="expense-pending-banner"
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs hover:bg-amber-500/15"
            >
              <AlertTriangle size={12} />
              <span className="flex-1">
                {tr(
                  `${data.pending_ocr} factures importades sense OCR — clica per processar-les gratis`,
                  `${data.pending_ocr} facturas importadas sin OCR — pulsa para procesarlas gratis`
                )}
              </span>
              <ArrowRight size={12} />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, trend, trendUp, icon, testid }) {
  return (
    <div className="card-surface p-3 bg-white/[0.02]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="font-mono text-lg font-semibold text-white mt-1 truncate">
        {value}
      </div>
      {trend !== null && trend !== undefined && Number.isFinite(trend) && (
        <div className={`text-[10px] flex items-center gap-1 mt-0.5
          ${trendUp ? "text-rose-300" : "text-emerald-300"}`}>
          {trendUp ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
          {trend > 0 ? "+" : ""}{trend.toFixed(0)}%
        </div>
      )}
    </div>
  );
}
